import React, { useState, useEffect } from "react";
import imageCompression from "browser-image-compression";
import { BsCheckCircle, BsCloudUpload, BsTrash } from "react-icons/bs";
import { useUploadImageToB2 } from "../store/api/ImageUploadApi";
import { Image, EyeOff, AlertCircle, Folder } from "lucide-react";

const SmartImageUpload = ({
  multiple = true,
  limit = 1,
  onSelectFiles,
  resetTrigger,
  files: externalFiles = [], // নতুন prop
}) => {
  const uploadImageToB2 = useUploadImageToB2();
  const [files, setFiles] = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // External files sync করুন
  useEffect(() => {
    if (externalFiles && externalFiles.length > 0) {
      const formattedFiles = externalFiles.map((f, idx) => {
        if (typeof f === "string") {
          // URL থেকে filename extract করুন
          const urlParts = f.split("/");
          const fileName = urlParts[urlParts.length - 1] || `Image-${idx + 1}`;
          return {
            url: f,
            preview: f,
            name: fileName,
            size: 0,
          };
        }
        return f;
      });
      setFiles(formattedFiles);
    } else if (externalFiles?.length === 0) {
      setFiles([]);
    }
  }, [externalFiles]);

  // Reset files if resetTrigger changes
  useEffect(() => {
    if (resetTrigger) {
      setFiles([]);
      onSelectFiles?.(multiple ? [] : null);
    }
  }, [resetTrigger]);

  const shortenFileName = (name, maxLength = 12) => {
    if (!name) return "Image";
    const dotIndex = name.lastIndexOf(".");
    const ext = dotIndex !== -1 ? name.slice(dotIndex) : "";
    const base = name.slice(0, dotIndex);
    return base.length > maxLength
      ? base.slice(0, maxLength) + "..." + ext
      : name;
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return "Unknown size";
    return bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(2)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const compressAndUpload = async (file, index) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: "image/webp",
    };
    try {
      setLoadingIndex(index);
      const compressed = await imageCompression(file, options);
      const renamed = new File([compressed], `${crypto.randomUUID()}.webp`, {
        type: "image/webp",
      });
      const response = await uploadImageToB2(renamed);

      // Response থেকে actual URL extract করুন
      const actualUrl = response?.data?.[0]?.imageURL || response;

      return {
        name: renamed.name,
        size: renamed.size,
        preview: URL.createObjectURL(renamed),
        url: actualUrl,
      };
    } catch (err) {
      console.error("❌ Compression or upload failed:", err);
      return null;
    } finally {
      setLoadingIndex(null);
    }
  };

  const processFiles = async (selectedFiles) => {
    const numericLimit = limit === "unlimited" ? Infinity : Number(limit);
    const allowedCount = Math.max(numericLimit - files.length, 0);

    // Limit exceed check
    if (allowedCount === 0) {
      alert(`Maximum ${limit} images allowed!`);
      return;
    }

    const limitedFiles = multiple
      ? selectedFiles.slice(0, allowedCount)
      : [selectedFiles[0]];

    const processed = [];
    for (let i = 0; i < limitedFiles.length; i++) {
      const result = await compressAndUpload(limitedFiles[i], i);
      if (result) {
        processed.push(result);
        if (!multiple) break;
      }
    }

    const updatedFiles = multiple
      ? [...files, ...processed]
      : processed.slice(0, 1);

    setFiles(updatedFiles);

    // পুরো updated list এর URLs পাঠান
    onSelectFiles?.(
      multiple ? updatedFiles.map((f) => f.url) : updatedFiles[0]?.url || null
    );
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files).filter((f) =>
      f.type.startsWith("image/")
    );
    processFiles(selected);
  };

  const handleRemove = (index) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);

    // পুরো updated list এর URLs পাঠান
    onSelectFiles?.(multiple ? updated.map((f) => f.url) : null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );
    processFiles(droppedFiles);
  };

  return (
    <div className="container mt-4">
      <div className="position-relative">
        {/* Main Upload Area */}
        <div
          className={`rounded-3 p-4 position-relative overflow-hidden ${
            dragActive ? "border-success" : ""
          }`}
          style={{
            border: dragActive ? "2px dashed #198754" : "2px dashed #dee2e6",
            background: dragActive
              ? "linear-gradient(135deg, #d1f4e0 0%, #e8f5e9 100%)"
              : "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            transition: "all 0.3s ease",
            opacity: limit !== "unlimited" && files.length >= limit ? 0.5 : 1,
            pointerEvents:
              limit !== "unlimited" && files.length >= limit ? "none" : "auto",
            cursor:
              limit !== "unlimited" && files.length >= limit
                ? "not-allowed"
                : "pointer",
          }}
          onDragOver={(e) => {
            if (limit !== "unlimited" && files.length >= limit) return;
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
        >
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              backgroundImage: dragActive
                ? "radial-gradient(circle, rgba(25, 135, 84, 0.1) 1px, transparent 1px)"
                : "none",
              backgroundSize: "20px 20px",
              opacity: 0.5,
              pointerEvents: "none",
            }}
          />

          <div className="">
            {/* Left: Icon + Text */}
            {/* <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: "48px",
                  height: "48px",
                  background: dragActive
                    ? "linear-gradient(135deg, #198754 0%, #20c997 100%)"
                    : "linear-gradient(135deg, #0a2628ff 0%, #1a4648 100%)",
                  transition: "all 0.3s ease",
                  transform: dragActive ? "scale(1.1)" : "scale(1)",
                }}
              >
                <BsCloudUpload
                  size={24}
                  className="text-white"
                  style={{
                    transition: "transform 0.3s ease",
                    transform: dragActive
                      ? "translateY(-2px)"
                      : "translateY(0)",
                  }}
                />
              </div>

              <div>
                <p
                  className="mb-1 fw-semibold"
                  style={{ color: "#0a2628ff", fontSize: "0.95rem" }}
                >
                  {limit !== "unlimited" && files.length >= limit
                    ? "Upload Limit Reached"
                    : dragActive
                    ? "Drop your images here"
                    : "Drag & Drop or Browse"}
                </p>
                <p className="mb-0 text-muted" style={{ fontSize: "0.75rem" }}>
                  {limit !== "unlimited" && files.length >= limit
                    ? "Remove some files to upload more"
                    : "Support: JPG, PNG, GIF (Max 10MB)"}
                </p>
              </div>
            </div> */}

            <label
              className={`btn mb-0 d-flex align-items-center gap-2${
                limit !== "unlimited" && files.length >= limit ? "disabled" : ""
              }`}
              style={{
                backgroundColor:
                  limit !== "unlimited" && files.length >= limit
                    ? "#6c757d"
                    : "#4316aaff",
                color: "white",
                fontWeight: "500",
                borderRadius: "10px",
                padding: "10px 24px",
                border: "none",
                transition: "all 0.3s ease",
                cursor:
                  limit !== "unlimited" && files.length >= limit
                    ? "not-allowed"
                    : "pointer",
              }}
              onMouseEnter={(e) => {
                if (limit === "unlimited" || files.length < limit) {
                  e.currentTarget.style.backgroundColor = "#1a4648";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(10, 38, 40, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (limit === "unlimited" || files.length < limit) {
                  e.currentTarget.style.backgroundColor = "#0a2628ff";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              <Folder size={16} />
              Browse Files
              <input
                type="file"
                accept="image/*"
                multiple={multiple}
                hidden
                disabled={limit !== "unlimited" && files.length >= limit}
                onChange={handleFileChange}
              />
            </label>
          </div>
        </div>

        {/* Upload Progress/Status */}
        {limit !== "unlimited" && (
          <div className="mt-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span
                className="small fw-semibold"
                style={{ color: "#0a2628ff" }}
              >
                Upload Progress
              </span>
              <span
                className={`badge ${
                  files.length >= limit
                    ? "bg-danger"
                    : files.length > limit * 0.7
                    ? "bg-warning"
                    : "bg-success"
                }`}
                style={{ fontSize: "0.75rem" }}
              >
                {files.length} / {limit} Files
              </span>
            </div>

            {/* Progress Bar */}
            <div
              className="progress"
              style={{
                height: "8px",
                borderRadius: "10px",
                backgroundColor: "#e9ecef",
              }}
            >
              <div
                className={`progress-bar ${
                  files.length >= limit
                    ? "bg-danger"
                    : files.length > limit * 0.7
                    ? "bg-warning"
                    : "bg-success"
                }`}
                style={{
                  width: `${(files.length / limit) * 100}%`,
                  transition: "width 0.4s ease",
                  borderRadius: "10px",
                }}
              />
            </div>

            {files.length >= limit && (
              <div
                className="alert alert-danger d-flex align-items-center gap-2 mt-2 mb-0 py-2 px-3"
                style={{ fontSize: "0.85rem", borderRadius: "8px" }}
              >
                <AlertCircle size={16} />
                <span>
                  You've reached the maximum upload limit. Remove files to add
                  more.
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <ul className="list-group list-group-flush mt-4">
        {files.map((file, index) => (
          <li
            key={file.url || index}
            className="list-group-item border-0 mb-2 rounded-3 position-relative overflow-hidden"
            style={{
              background: "linear-gradient(to right, rgba(25, 135, 84, 0.15)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                "linear-gradient(to right, #d1f4e0, #e8f5e9)";
              e.currentTarget.style.transform = "translateX(4px)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(25, 135, 84, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                "linear-gradient(to right, rgba(25, 135, 84, 0.15), rgba(25, 135, 84, 0.1))";
              e.currentTarget.style.transform = "translateX(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {/* Progress bar at bottom */}
            <div
              className="position-absolute bottom-0 start-0 bg-success"
              style={{
                height: "3px",
                width: "100%",
                opacity: 0,
                transition: "opacity 0.3s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
            />

            <div className="d-flex align-items-center justify-content-between py-2">
              {/* Left: Image + Info */}
              <div className="d-flex align-items-center gap-3 flex-grow-1 min-width-0">
                {/* Image Preview */}
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="position-relative d-block"
                  style={{ textDecoration: "none" }}
                >
                  <div
                    className="position-relative overflow-hidden"
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "12px",
                      border: "2px solid #e9ecef",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#198754";
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e9ecef";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <img
                      src={file.preview || file.url}
                      alt="preview"
                      className="w-100 h-100"
                      style={{
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.transform = "scale(1.1)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    />
                    {/* Hover overlay */}
                    {/* <div
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                      style={{
                        background: "rgba(0, 0, 0, 0.5)",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.opacity = "1")
                      }
                    >
                      <EyeOff className="text-white" size={20} />
                    </div> */}
                  </div>
                </a>

                {/* File Info */}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div
                    className="fw-semibold text-truncate mb-1"
                    style={{
                      maxWidth: "100%",
                      color: "#212529",
                      fontSize: "0.95rem",
                    }}
                    title={file.name}
                  >
                    {shortenFileName(file.name)}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="badge bg-success bg-opacity-10 text-success border-0 d-flex align-items-center gap-1"
                      style={{ fontSize: "0.7rem", padding: "4px 8px" }}
                    >
                      <BsCheckCircle size={12} />
                      Uploaded
                    </span>
                    {/* {file.size && (
                      <>
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.7rem" }}
                        >
                          •
                        </span>
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.7rem" }}
                        >
                          {file.size}
                        </span>
                      </>
                    )} */}
                  </div>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="d-flex align-items-center gap-2 ms-2">
                {/* Success Icon */}
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
                  style={{
                    width: "36px",
                    height: "36px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#198754";
                    e.currentTarget.querySelector("svg").style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(25, 135, 84, 0.1)";
                    e.currentTarget.querySelector("svg").style.color =
                      "#198754";
                  }}
                >
                  <BsCheckCircle
                    className="text-success"
                    size={18}
                    style={{ transition: "color 0.3s ease" }}
                  />
                </div>

                {/* Delete Button */}
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center justify-content-center"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    border: "none",
                    background: "rgba(220, 53, 69, 0.1)",
                    color: "#dc3545",
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => handleRemove(index)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#dc3545";
                    e.currentTarget.style.color = "white";
                    e.currentTarget.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(220, 53, 69, 0.1)";
                    e.currentTarget.style.color = "#dc3545";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <BsTrash size={16} />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {loadingIndex !== null && (
        <div className="mt-3">
          <div
            className="d-flex align-items-center justify-content-center gap-3 p-3 rounded-3"
            style={{
              background: "linear-gradient(135deg, #e7f3ff 0%, #f0f8ff 100%)",
              border: "1px solid rgba(13, 110, 253, 0.2)",
            }}
          >
            {/* Animated Spinner */}
            <div className="position-relative">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{
                  width: "32px",
                  height: "32px",
                  borderWidth: "3px",
                }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              {/* Pulse effect */}
              <div
                className="position-absolute top-50 start-50 translate-middle rounded-circle"
                style={{
                  width: "40px",
                  height: "40px",
                  background: "rgba(13, 110, 253, 0.2)",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </div>

            {/* Text */}
            <div className="text-start">
              <p
                className="mb-1 fw-semibold text-primary"
                style={{ fontSize: "0.95rem" }}
              >
                Uploading Image {loadingIndex + 1}
              </p>
              <p className="mb-0 text-muted" style={{ fontSize: "0.75rem" }}>
                Please wait while we process your image...
              </p>
            </div>

            {/* Animated dots */}
            <div className="d-flex gap-1 ms-auto">
              <div
                className="rounded-circle bg-primary"
                style={{
                  width: "6px",
                  height: "6px",
                  animation: "bounce 1s ease-in-out infinite",
                }}
              />
              <div
                className="rounded-circle bg-primary"
                style={{
                  width: "6px",
                  height: "6px",
                  animation: "bounce 1s ease-in-out 0.2s infinite",
                }}
              />
              <div
                className="rounded-circle bg-primary"
                style={{
                  width: "6px",
                  height: "6px",
                  animation: "bounce 1s ease-in-out 0.4s infinite",
                }}
              />
            </div>
          </div>

          {/* CSS Animations */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
        @keyframes pulse {
          0%, 100% {
            opacity: 0.4;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(1.5);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
      `,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SmartImageUpload;
